/**
 * @file QueRecherchezVousCrossfit.tsx
 * @summary Article de blog — QueRecherchezVousCrossfit
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import SEO from "@/components/SEO";
import BlogHeroImage, { getArticleImageUrls, withArticleImages, slugFromUrl } from "@/components/blog/BlogHeroImage";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowLeft, CalendarCheck, MessageSquare, Dumbbell, Heart, Trophy, Users, Apple, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import BlogCTA, { BlogStickyCTA } from "@/components/blog/BlogCTA";

const URL = "https://crossfitchistera.fr/blog/que-recherchez-vous-dans-le-crossfit";
const SLUG = slugFromUrl(URL);
const IMAGES = getArticleImageUrls(SLUG, 5);
const RESAWOD = "https://social.resawod.com/fr/box/crossfit-chistera/reserver";

const faq = [
  {
    "@type": "Question",
    name: "Faut-il être déjà sportif pour commencer le CrossFit ?",
    acceptedAnswer: { "@type": "Answer", text: "Non. Les séances peuvent être adaptées au niveau de chacun. Un débutant ne fait pas nécessairement les mêmes charges, les mêmes amplitudes ou les mêmes mouvements qu'un compétiteur." },
  },
  {
    "@type": "Question",
    name: "Peut-on faire du CrossFit avec une douleur ou une blessure ?",
    acceptedAnswer: { "@type": "Answer", text: "Cela dépend de la douleur, de la blessure, du stade de récupération et des consignes médicales. CrossFit Chistera ne remplace pas une rééducation médicale, mais peut accompagner une reprise autorisée et progressive." },
  },
  {
    "@type": "Question",
    name: "Le CrossFit est-il adapté après 50 ans ?",
    acceptedAnswer: { "@type": "Answer", text: "Oui, si l'entraînement est adapté. Après 50 ans, le renforcement musculaire, la mobilité, l'équilibre et le cardio sont essentiels." },
  },
  {
    "@type": "Question",
    name: "Est-ce adapté si je suis en surpoids ?",
    acceptedAnswer: { "@type": "Answer", text: "Oui, à condition de commencer progressivement et dans un cadre bienveillant. L'objectif est de reconstruire une relation durable au mouvement, avec un coach attentif et, si nécessaire, un accompagnement nutritionnel." },
  },
  {
    "@type": "Question",
    name: "Le CrossFit peut-il aider pour HYROX ou ATHX ?",
    acceptedAnswer: { "@type": "Answer", text: "Oui. Le CrossFit développe force, endurance musculaire, cardio, coordination et capacité à travailler sous fatigue. Il faut compléter avec une préparation spécifique : course, rameur, ski erg, transitions, pacing." },
  },
  {
    "@type": "Question",
    name: "Peut-on préparer un marathon ou la SaintéLyon avec le CrossFit ?",
    acceptedAnswer: { "@type": "Answer", text: "Le CrossFit est un excellent complément pour renforcer le corps, améliorer le gainage et mieux tolérer la charge. Mais la course reste spécifique : il faut conserver une programmation running adaptée à l'objectif." },
  },
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Que recherchez-vous dans le CrossFit ?",
    description: "Reprise du sport, douleurs, perte de poids, HYROX, ATHX, compétition ou préparation physique : découvrez comment CrossFit Chistera adapte le CrossFit à votre profil.",
    author: { "@type": "Organization", name: "CrossFit Chistera" },
    publisher: { "@type": "Organization", name: "CrossFit Chistera" },
    mainEntityOfPage: URL,
    inLanguage: "fr-FR",
  },
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faq },
];

const profils = [
  { icon: Users, title: "Actif qui reprend", need: "Routine, cadre, énergie", answer: "Cours coachés, progressivité, groupe", warn: "Ne pas reprendre trop fort" },
  { icon: Dumbbell, title: "Sportif déjà actif", need: "Complément physique", answer: "Force, cardio, mobilité, prévention", warn: "Gérer la charge totale" },
  { icon: Heart, title: "+ 50 ans", need: "Force, autonomie, mobilité", answer: "Renforcement adapté, équilibre", warn: "Adapter intensité et récupération" },
  { icon: ShieldCheck, title: "Blessé / rééducation", need: "Bouger sans aggraver", answer: "Adaptation des mouvements, accord médical", warn: "Ne remplace pas le suivi santé" },
  { icon: Heart, title: "Personne en surpoids", need: "Reprise sans jugement", answer: "Coaching, variantes, nutrition", warn: "Éviter la logique punitive" },
  { icon: Trophy, title: "HYROX / ATHX", need: "Objectif mesurable", answer: "Planification, force, endurance, pacing", warn: "Ne pas faire que des séances dures" },
  { icon: Trophy, title: "Compétiteur CrossFit", need: "Polyvalence", answer: "Skills, haltéro, conditioning, stratégie", warn: "Technique avant intensité" },
  { icon: Trophy, title: "Marathon / SaintéLyon", need: "Robustesse", answer: "Renforcement, gainage, prévention", warn: "Garder la spécificité course" },
  { icon: ShieldCheck, title: "Examen physique", need: "Performance utile", answer: "Force relative, endurance, mental", warn: "Coller aux exigences réelles" },
  { icon: Users, title: "Ancien sportif", need: "Retrouver niveau et collectif", answer: "Reprise structurée, mental, communauté", warn: "Accepter la reconstruction" },
];

const coachs = [
  { name: "Benbou", role: "Force, haltérophilie, exigence technique", profile: "Haltérophilie · Compétition CrossFit · ATHX · Préparation physique exigeante" },
  { name: "Cédric", role: "Endurance, cardio, esprit d'équipe", profile: "Endurance · HYROX · Course sous fatigue · Anciens sportifs collectifs" },
  { name: "David", role: "Discipline, mental, dépassement", profile: "Concours police, pompiers, armée · Mental · Cadre et structure" },
  { name: "Perrine", role: "Exigence bienveillante, énergie positive", profile: "Débutants · Reprise du sport · Personnes intimidées · Confiance" },
  { name: "Orni", role: "Préparation mentale, accompagnement global", profile: "Running · Hybrid Training · Reprise progressive · Préparation mentale" },
];

function CTAButtons({ variant = "default" }: { variant?: "default" | "hero" }) {
  return (
    <div className={`flex flex-col sm:flex-row gap-3 ${variant === "hero" ? "justify-start" : "justify-center"}`}>
      <Button asChild size="lg" className="bg-cta text-cta-foreground hover:bg-cta/90 font-body normal-case">
        <a href={RESAWOD} target="_blank" rel="noopener noreferrer">
          <CalendarCheck className="w-4 h-4" /> Réserver un créneau
        </a>
      </Button>
      <Button asChild size="lg" variant="outline" className="border-gold text-gold hover:bg-gold hover:text-gold-foreground font-body normal-case">
        <Link to="/#contact">
          <MessageSquare className="w-4 h-4" /> Parler à un coach
        </Link>
      </Button>
    </div>
  );
}

export default function QueRecherchezVousCrossfit() {
  return (
    <>
      <SEO
        title="Que recherchez-vous dans le CrossFit ? | CrossFit Chistera"
        description="Reprise du sport, douleurs, perte de poids, HYROX, ATHX, compétition : comment CrossFit Chistera adapte le CrossFit à votre profil."
        canonical={URL}
        jsonLd={withArticleImages(jsonLd, IMAGES)}
          ogImages={IMAGES}
      />
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-20">
          <article className="container mx-auto px-4 max-w-4xl">
            <BlogHeroImage slug={SLUG} />
            {/* Breadcrumb */}
            <nav aria-label="Fil d'Ariane" className="mb-6 text-sm text-muted-foreground flex items-center flex-wrap gap-1">
              <Link to="/" className="inline-flex items-center gap-1 hover:text-foreground">
                <ArrowLeft className="w-3 h-3" /> Accueil
              </Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-muted-foreground">Blog</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-foreground">Que recherchez-vous dans le CrossFit ?</span>
            </nav>

            {/* Hero */}
            <header className="mb-10">
              <span className="inline-block text-xs font-semibold text-gold tracking-wider mb-3 font-body normal-case uppercase">Méthode Chistera · Lecture ~ 10 min</span>
              <h1 className="text-4xl md:text-6xl text-foreground mb-6 font-heading">Que recherchez-vous dans le CrossFit ?</h1>
              <p className="text-lg text-foreground/85 leading-relaxed mb-8">
                Reprise après des années d'arrêt, douleurs anciennes, perte de poids, complément à un autre sport, préparation HYROX, ATHX, compétition CrossFit, marathon, SaintéLyon ou concours physique : la bonne question n'est pas <em>« est-ce fait pour moi ? »</em>, mais <strong>« qu'est-ce que je viens chercher, et comment l'entraînement peut être adapté à mon corps et à mon objectif ? »</strong>
              </p>
              <CTAButtons variant="hero" />
            </header>

            {/* Réponse courte */}
            <aside className="border-l-4 border-gold bg-charcoal/40 px-6 py-5 mb-12 rounded-r-md">
              <p className="text-sm uppercase tracking-wider text-gold font-heading mb-2">Réponse courte</p>
              <p className="text-foreground/90 leading-relaxed">
                Vous pouvez rechercher dans le CrossFit : reprendre une activité physique, être accompagné par un coach, perdre du poids, améliorer votre cardio, devenir plus fort, bouger sans douleur excessive, préparer une compétition, retrouver une routine ou simplement vous sentir mieux dans votre corps. La valeur du CrossFit dépend de l'<strong>adaptation</strong> : charge, volume, amplitude, intensité, technique et récupération doivent être ajustés à la personne.
              </p>
            </aside>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground prose-li:text-foreground/85">

              <h2>Pourquoi le CrossFit attire autant de profils différents ?</h2>
              <p>
                Le CrossFit repose sur des mouvements fonctionnels, variés, réalisés avec une intensité adaptée. Une même séance peut être faite par un compétiteur, un débutant, une personne de plus de 50 ans ou quelqu'un qui revient d'une blessure — à condition que le coach ajuste les paramètres. Une seule pratique développe progressivement la force, l'endurance cardio, la mobilité, la coordination, la puissance, l'équilibre, la confiance et la régularité.
              </p>

              <h2>Si vous êtes un actif qui veut reprendre le sport</h2>
              <p>
                Vous travaillez beaucoup, vous êtes souvent assis, vos horaires sont chargés. Votre problème n'est pas le manque de volonté — c'est le manque de cadre. Le CrossFit y répond parce que la séance est <strong>préparée, encadrée et collective</strong>. L'objectif des premières semaines : retrouver une routine, apprendre les mouvements de base, reconstruire une capacité cardio, sortir de la sédentarité sans brûler les étapes.
              </p>
              <p className="not-prose"><Link to="/reprendre-le-sport" className="text-gold hover:underline">→ Lire : Reprendre le sport chez Chistera</Link></p>

              <h2>Si vous faites déjà un autre sport</h2>
              <p>
                Course, rugby, vélo, foot, natation, trail, boxe : le CrossFit peut devenir un complément puissant. Un coureur peut manquer de force et de gainage ; un nageur de stabilité au sol ; un joueur collectif de cardio continu. Le CrossFit comble ces angles morts — à condition de doser. Si vous avez déjà 3 sorties par semaine, le CrossFit doit compléter, pas écraser.
              </p>

              <h2>Si vous avez plus de 50 ans</h2>
              <p>
                Après 50 ans, l'objectif n'est pas seulement de « faire du cardio ». C'est de préserver ce qui vous rend autonome : <strong>force, équilibre, mobilité, masse musculaire, coordination</strong>. La NSCA souligne que l'entraînement en résistance est une intervention essentielle contre la perte de force et la fragilité. Chez Chistera, on adapte les charges, les sauts, la course, la profondeur des squats et les temps de récupération — sans infantiliser.
              </p>

              <h2>Si vous êtes blessé ou en rééducation</h2>
              <div className="not-prose border border-gold/30 bg-charcoal/40 rounded-lg p-5 my-6 flex gap-3">
                <ShieldCheck className="w-5 h-5 text-gold shrink-0 mt-1" />
                <p className="text-foreground/90 text-sm leading-relaxed">
                  Une box ne remplace ni un médecin, ni un chirurgien, ni un kinésithérapeute. Toute reprise doit être autorisée et suivie par les professionnels compétents. CrossFit Chistera peut <strong>accompagner une reprise progressive</strong>, pas la remplacer.
                </p>
              </div>
              <p>
                Une fois la reprise autorisée, le coach peut adapter : box jumps remplacés par des step-ups, course par du rameur ou du ski erg, squats profonds par une amplitude réduite, mouvements overhead par des variantes plus stables, charges lourdes par un travail technique contrôlé. Le principe : <em>continuer à bouger quand c'est possible, sans confondre courage et imprudence</em>.
              </p>

              <h2>Si vous êtes en surpoids et voulez refaire du sport sans jugement</h2>
              <p>
                Une box bien coachée doit accueillir sérieusement et respectueusement. L'enjeu n'est pas de « se punir » ni de brûler des calories à tout prix, mais de <strong>reconstruire une relation durable au mouvement</strong>. Bouger régulièrement, augmenter progressivement la dépense, renforcer jambes, dos et tronc, améliorer le souffle, créer une routine — et associer entraînement, nutrition, sommeil et récupération.
              </p>

              <h2>Si vous préparez HYROX, ATHX, une compétition CrossFit, un marathon ou la SaintéLyon</h2>
              <p>
                Le CrossFit est aussi une base solide pour préparer des échéances exigeantes. Pour <strong>HYROX</strong> : course sous fatigue, sled push/pull, farmer carry, sandbag lunges, wall balls, ski erg, rameur, pacing. Pour <strong>ATHX</strong> : strict press, back squat, deadlift, run/row, metcon long, stratégie d'effort sur 2h30. Pour le <strong>compétiteur CrossFit</strong> : haltérophilie, gymnastique, conditioning, standards techniques, gestion des WODs.
              </p>

              <h2>Si vous préparez un examen physique : police, pompiers, armée</h2>
              <p>
                Endurance, puissance, gainage, force relative, capacité à répéter des efforts, mental, gestion du stress : le CrossFit habitue à produire des efforts variés dans un cadre chronométré. Mais il faut rester spécifique : un test Luc Léger, une épreuve de traction ou un parcours d'obstacles ne se préparent pas exactement de la même manière. Un coaching individualisé permet de coller aux exigences réelles.
              </p>

              <h2>Si vous êtes un ancien sportif qui veut reprendre</h2>
              <p>
                L'ancien sportif a un avantage — il connaît l'effort — et un piège : vouloir reprendre comme s'il n'avait jamais arrêté. Les blessures n'arrivent pas parce que le sport est dangereux, mais parce que la reprise est trop rapide. Le bon objectif : accepter une phase de reconstruction, retrouver les bases techniques, reprendre la force progressivement, retrouver le plaisir du collectif.
              </p>

              <h2>La méthode Chistera : adapter avant d'intensifier</h2>
              <p>
                Cela ne veut pas dire que les séances sont faciles. Cela veut dire qu'elles sont <strong>construites pour être utiles</strong>. Comprendre ce qu'on travaille, pourquoi, quelle variante choisir, quelle charge utiliser, quel rythme tenir, quand pousser, quand ralentir, quand s'arrêter. C'est ce qui permet de réunir dans une même box des débutants, des seniors, des cadres actifs, des sportifs confirmés et des compétiteurs.
              </p>
            </div>

            {/* Tableau des profils */}
            <section className="my-16">
              <h2 className="text-3xl font-heading text-foreground mb-6">Que recherchez-vous dans le CrossFit ?</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {profils.map((p) => {
                  const Icon = p.icon;
                  return (
                    <div key={p.title} className="border border-border bg-card rounded-lg p-5 hover:border-gold/50 transition-colors">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-gold/15 text-gold flex items-center justify-center shrink-0">
                          <Icon className="w-5 h-5" />
                        </div>
                        <h3 className="font-heading text-lg text-foreground">{p.title}</h3>
                      </div>
                      <dl className="text-sm space-y-1.5 font-body normal-case">
                        <div><dt className="inline text-muted-foreground">Besoin : </dt><dd className="inline text-foreground/90">{p.need}</dd></div>
                        <div><dt className="inline text-gold">Réponse Chistera : </dt><dd className="inline text-foreground/90">{p.answer}</dd></div>
                        <div><dt className="inline text-muted-foreground">Vigilance : </dt><dd className="inline text-foreground/70">{p.warn}</dd></div>
                      </dl>
                    </div>
                  );
                })}
              </div>
            </section>

            <BlogCTA single primaryLabel="Réserver mon essai gratuit" />

            {/* CTA mid-article */}
            <div className="my-16 border border-gold/30 bg-charcoal/40 rounded-lg px-6 py-8 text-center">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground mb-3">Et vous, qu'est-ce que vous cherchez ?</h2>
              <p className="text-foreground/80 mb-6 max-w-xl mx-auto font-body normal-case">
                2 séances d'essai gratuites pour découvrir la box et discuter de votre objectif avec un coach.
              </p>
              <CTAButtons />
            </div>

            {/* Coachs */}
            <section className="my-16">
              <h2 className="text-3xl font-heading text-foreground mb-2">Les coachs : des profils complémentaires</h2>
              <p className="text-foreground/80 mb-6 font-body normal-case">Selon ce que vous cherchez, un coach peut être plus particulièrement aligné avec votre objectif.</p>
              <div className="grid md:grid-cols-2 gap-4">
                {coachs.map((c) => (
                  <div key={c.name} className="border border-border bg-card rounded-lg p-5">
                    <h3 className="font-heading text-xl text-gold mb-1">{c.name}</h3>
                    <p className="text-foreground/90 font-semibold text-sm mb-2 font-body normal-case">{c.role}</p>
                    <p className="text-foreground/70 text-sm font-body normal-case">{c.profile}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Nutrition */}
            <section className="my-16">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gold/15 text-gold flex items-center justify-center shrink-0">
                  <Apple className="w-5 h-5" />
                </div>
                <h2 className="text-3xl font-heading text-foreground m-0">La nutrition : un pilier inséparable</h2>
              </div>
              <p className="text-foreground/85 leading-relaxed font-body normal-case">
                On ne peut pas parler sérieusement de reprise, de perte de poids, de compétition ou de récupération sans parler de nutrition. L'alimentation influence l'énergie, la récupération, la progression musculaire, la gestion du poids et la qualité du sommeil. Pour une personne qui reprend, le sujet n'est pas un régime extrême : c'est <strong className="text-foreground">manger suffisamment, mieux répartir les protéines, hydrater correctement, préparer les repas autour des séances</strong>. Pour un compétiteur, la nutrition devient stratégique : carburant, timing, électrolytes, récupération.
              </p>
            </section>

            {/* FAQ */}
            <section className="my-16">
              <h2 className="text-3xl font-heading text-foreground mb-6">Questions fréquentes</h2>
              <div className="space-y-4">
                {faq.map((q) => (
                  <details key={q.name} className="group border border-border bg-card rounded-lg p-5 open:border-gold/50">
                    <summary className="cursor-pointer font-heading text-lg text-foreground flex justify-between items-center gap-4 list-none">
                      <span>{q.name}</span>
                      <ChevronRight className="w-5 h-5 text-gold shrink-0 transition-transform group-open:rotate-90" />
                    </summary>
                    <p className="mt-3 text-foreground/85 leading-relaxed font-body normal-case">{q.acceptedAnswer.text}</p>
                  </details>
                ))}
              </div>
            </section>

            {/* Conclusion + CTA final */}
            <section className="my-16">
              <h2 className="text-3xl font-heading text-foreground mb-4">Votre objectif décide de votre CrossFit</h2>
              <p className="text-foreground/85 leading-relaxed mb-6 font-body normal-case">
                Vous ne cherchez peut-être pas la même chose que votre voisin de séance. Lui prépare HYROX. Vous reprenez après dix ans d'arrêt. Une autre personne veut perdre du poids ou retrouver de la force après 50 ans. C'est précisément pour cela que le CrossFit, lorsqu'il est <strong>coaché, adapté et intégré dans un objectif personnel</strong>, devient puissant.
              </p>
              <p className="text-foreground/85 leading-relaxed mb-8 font-body normal-case">
                La promesse Chistera : un cadre exigeant mais accessible, une communauté sans jugement, une méthode adaptée à chacun.
              </p>
              <div className="border border-gold/30 bg-gradient-to-br from-charcoal/60 to-charcoal/20 rounded-lg px-6 py-10 text-center">
                <h3 className="text-2xl md:text-3xl font-heading text-foreground mb-3">Prêt(e) à démarrer ?</h3>
                <p className="text-foreground/80 mb-6 max-w-xl mx-auto font-body normal-case">Réservez votre créneau ou échangez avec un coach avant de venir.</p>
                <CTAButtons />
              </div>
            </section>

            {/* Maillage interne */}
            <section className="my-12 border-t border-border pt-8">
              <h2 className="text-xl font-heading text-foreground mb-4">À lire aussi</h2>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm font-body normal-case">
                <li><Link to="/crossfit-debutant" className="text-gold hover:underline">→ CrossFit débutant</Link></li>
                <li><Link to="/reprendre-le-sport" className="text-gold hover:underline">→ Reprendre le sport</Link></li>
                <li><Link to="/cours/wod" className="text-gold hover:underline">→ Le cours WOD CrossFit</Link></li>
                <li><Link to="/cours/hybrid-training" className="text-gold hover:underline">→ Hybrid Training (HYROX)</Link></li>
                <li><Link to="/lieux" className="text-gold hover:underline">→ La box & les communes desservies</Link></li>
                <li><Link to="/lexique" className="text-gold hover:underline">→ Lexique CrossFit</Link></li>
              </ul>
            </section>

            {/* Sources */}
            <section className="my-12 text-xs text-muted-foreground font-body normal-case">
              <h2 className="text-sm font-heading text-foreground mb-2 uppercase tracking-wider">Sources</h2>
              <ul className="space-y-1 list-disc list-inside">
                <li><a href="https://www.crossfit.com/what-is-crossfit-a" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-gold">CrossFit — What is CrossFit</a></li>
                <li><a href="https://www.who.int/publications/i/item/9789240014886" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-gold">OMS — Lignes directrices activité physique</a></li>
                <li><a href="https://www.nsca.com/about-us/position-statements/resistance-training-for-older-adults/" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-gold">NSCA — Resistance Training for Older Adults</a></li>
                <li><a href="https://bjsm.bmj.com/content/57/9/500" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-gold">Aspetar / BJSM — Réhabilitation post-LCA</a></li>
                <li><a href="https://hyrox.com/the-fitness-race/" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-gold">HYROX — The Fitness Race</a></li>
                <li><a href="https://events.athxgames.com/workouts/2026" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-gold">ATHX Games — Workouts 2026</a></li>
                <li><a href="https://pubmed.ncbi.nlm.nih.gov/26891166/" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-gold">ACSM — Nutrition and Athletic Performance</a></li>
              </ul>
            </section>

          </article>
                  <BlogStickyCTA />
        </main>
        <Footer />
      </div>
    </>
  );
}
